import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { useAuth } from 'context'
import * as controllers from 'controllers'
import { PersonDto, PersonReportsDto } from 'dtos'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import ReportsEditDialog from './ReportsEditDialog'

interface Props {
  person: PersonDto
}

export default function ReportsCard({ person }: Props) {
  const { permissions } = useAuth()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [isGettingReports, setIsGettingReports] = useState(false)
  const [isSavingReports, setIsSavingReports] = useState(false)
  const [personReports, setPersonReports] = useState<PersonReportsDto>(new PersonReportsDto())

  useEffect(() => {
    if (!person.personGuid) {
      return
    }

    setIsGettingReports(true)
    controllers.getPersonReports(person.personGuid)
      .then(response => setPersonReports(response.value))
      .finally(() => setIsGettingReports(false))
  }, [person.personGuid])

  return (
    <>
      <ReportsEditDialog
        initialValues={personReports}
        loading={isSavingReports}
        onClose={() => setDialogOpen(false)}
        onSave={values => {
          setIsSavingReports(true)
          controllers.updatePersonReports(person.personGuid!, values)
            .then(response => {
              setPersonReports(response.value)
              setDialogOpen(false)
              enqueueSnackbar('Reports Saved Successfully!', { variant: 'success' })
            })
            .finally(() => setIsSavingReports(false))
        }}
        open={dialogOpen}
      />

      <Card id='reports-card'>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Typography fontWeight='bold' variant='h5'>
                Reports
              </Typography>
              {permissions.ADMINISTRATION_ADD_EDIT && (
                <Button endIcon={<Edit />} id='reports-edit' onClick={() => setDialogOpen(true)} variant='text'>
                  EDIT
                </Button>
              )}
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            {isGettingReports && (
              <Grid item xs={12} container justifyContent='center'>
                <CircularProgress color='primary' size={50} />
              </Grid>
            )}
            {!isGettingReports && personReports.reports.length === 0 && (
              <Grid item xs={12}>
                <Typography>No reports assigned</Typography>
              </Grid>
            )}
            {!isGettingReports &&
              personReports.reports.map(report => (
                <Grid item xs={12} key={report.reportGuid}>
                  <Typography>{report.name}</Typography>
                  {report.description && <Typography variant='body2'>{report.description}</Typography>}
                </Grid>
              ))}
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
